/**
 * S22 — mounts SwimlaneView for an active view carrying a `flow <Ref>` clause.
 * The view comes from the SAME projection machinery every other surface uses
 * (`useViewSurface` → `computeViewSurfaceResult`): the tab-aware effective
 * name, view-INSTANCE parent resolution (an instance of a flow-carrying parent
 * renders the inherited flow), live knob bindings, and the crash guard — a
 * throwing walk/layout degrades to nothing-rendered + a HUD diagnostic, never
 * an unmounted tree (same posture as the table/matrix containers).
 *
 * Renders the referenced process as a static flow (no playback: empty walker,
 * noop step/branch handlers). Interactive playback rides the process-tab path
 * (ProcessMode); a flow VIEW is a read surface. Returns null when no flow view
 * is active.
 */
import { useMemo, type ReactNode } from "react";
import { computeFlowData, STATIC_WALKER_STATE } from "./flowData.js";
import { SwimlaneView } from "../process/SwimlaneView.js";
import { BpmnView } from "../process/BpmnView.js";
import { makeBpmnLaneEditApi } from "../process/bpmnLaneEdit.js";
import { useModelMutationsOptional } from "../workbench/mutations.js";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useViewSurface } from "./useViewSurface.js";

const noop = () => {};

/** Write-back coordinates for the on-diagram rail editor — computed from the
 *  REGISTERED source view (already vetted write-back-eligible by
 *  `useViewSurface`: not a live draft / knob-bound / instance projection). Its
 *  clauses ARE the source bytes, so `flowClauseIndex` addresses the same clause
 *  the engine mutation patches. Null when the view carries no flow clause or has
 *  no qualified name. */
function flowWriteCoords(
  view: { readonly name?: string; readonly ownerPath?: readonly string[]; readonly clauses: readonly { readonly kind: string }[] } | null,
): { viewName: string; flowClauseIndex: number; clauseCount: number } | null {
  if (!view) return null;
  const flowClauseIndex = view.clauses.findIndex((c) => c.kind === "flow");
  if (flowClauseIndex < 0) return null;
  const flow = view.clauses[flowClauseIndex] as { readonly body?: readonly unknown[] };
  const name = [...(view.ownerPath ?? []), view.name ?? ""].filter(Boolean).join(".");
  if (!name) return null;
  return { viewName: name, flowClauseIndex, clauseCount: flow.body?.length ?? 0 };
}

export function FlowContainer({ editable = false }: { editable?: boolean }): ReactNode {
  const { resolvedModel } = useWorkspaceModel();
  const mutations = useModelMutationsOptional();
  // The EFFECTIVE view: instance-resolved (parent clauses merged), knob-bound,
  // crash-guarded — null when no view is active or the projection threw.
  // `registeredView` is the write-back-eligible source object (null unless the
  // active view is a registered, non-draft, non-knob-bound, non-instance view).
  const { view, registeredView } = useViewSurface();
  const data = useMemo(() => {
    if (!resolvedModel || !view) return null;
    try {
      return computeFlowData(view, resolvedModel);
    } catch (e) {
      // Same crash posture as the board/table/matrix: a throwing walk or
      // layout renders no flow (the HUD carries the view's diagnostics) —
      // never an unmounted tree.
      console.debug("[view-flow] flow data evaluation failed:", e);
      return null;
    }
  }, [resolvedModel, view]);
  const laneEdit = useMemo(() => {
    if (!editable || !mutations) return undefined;
    const coords = flowWriteCoords(registeredView ?? null);
    if (!coords) return undefined;
    const reassign =
      data?.kind === "bpmn"
        ? { stepByIndex: data.stepByIndex, reassignTargets: data.reassignTargets, processHandle: data.processHandle }
        : {};
    return makeBpmnLaneEditApi({ mutations, ...coords, ...reassign });
  }, [editable, mutations, registeredView, data]);
  if (!data) return null;
  if (data.kind === "bpmn") {
    // `flow X bpmn { … }` — the laned horizontal BPMN, a read surface for
    // playback (that rides the process-tab path) but an EDIT surface for the
    // lane/pool rail when the host opts in (`editable`).
    return (
      <div className="arch-flow-view h-full w-full overflow-auto">
        <BpmnView
          layout={data.layout}
          activeStepIndex={null}
          onSelectStepIndex={noop}
          onSubprocessToggle={noop}
          onSelectModule={noop}
          edit={laneEdit}
        />
      </div>
    );
  }
  return (
    <div className="arch-flow-view h-full w-full overflow-auto">
      <SwimlaneView
        layout={data.layout}
        walkResult={data.walk}
        walkerState={STATIC_WALKER_STATE}
        activeStepIndex={null}
        onSelectStepIndex={noop}
        onBranchSelect={noop}
        onSubprocessToggle={noop}
        onSelectModule={noop}
      />
    </div>
  );
}
